import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RefreshCw, Eye, CheckCircle, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";

interface ZoomDebugLog {
  id: string;
  created_at: string;
  meeting_id: string | null;
  mentorship_request_id: string | null;
  action: 'create' | 'update' | 'delete';
  endpoint: string;
  payload_masked: Record<string, unknown> | null;
  response_masked: Record<string, unknown> | null;
  status_code: number | null;
  error_message: string | null;
}

export function ZoomDebugLogs() {
  const [selectedLog, setSelectedLog] = useState<ZoomDebugLog | null>(null);

  const { data: logs, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["zoom-debug-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("zoom_api_debug_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as ZoomDebugLog[];
    },
  });

  const getStatusBadge = (statusCode: number | null, errorMessage: string | null) => {
    if (errorMessage) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }
    if (statusCode && statusCode >= 200 && statusCode < 300) {
      return (
        <Badge variant="default" className="bg-green-600 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          {statusCode}
        </Badge>
      );
    }
    if (statusCode) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          {statusCode}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        Unknown
      </Badge>
    );
  };

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      create: "bg-blue-600",
      update: "bg-amber-600",
      delete: "bg-red-600",
    };
    return (
      <Badge className={colors[action] || "bg-gray-600"}>
        {action.toUpperCase()}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            Zoom API Debug Logs
          </CardTitle>
          <CardDescription>
            View masked payloads and responses from Zoom API calls (last 50 entries)
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !logs || logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No Zoom API debug logs found. Logs will appear here when meetings are created.
          </div>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {getActionBadge(log.action)}
                        {getStatusBadge(log.status_code, log.error_message)}
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(log.created_at), "MMM d, yyyy h:mm:ss a")}
                        </span>
                      </div>
                      
                      <div className="text-sm font-mono bg-muted px-2 py-1 rounded mb-2 truncate">
                        {log.endpoint}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Meeting ID:</span>{" "}
                          {log.meeting_id ? (
                            <span className="font-mono">{log.meeting_id.substring(0, 8)}...</span>
                          ) : (
                            <span className="italic">N/A</span>
                          )}
                        </div>
                        <div>
                          <span className="font-medium">Request ID:</span>{" "}
                          {log.mentorship_request_id ? (
                            <span className="font-mono">{log.mentorship_request_id.substring(0, 8)}...</span>
                          ) : (
                            <span className="italic">N/A</span>
                          )}
                        </div>
                      </div>
                      
                      {log.error_message && (
                        <div className="mt-2 text-sm text-destructive">
                          {log.error_message}
                        </div>
                      )}
                    </div>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            {getActionBadge(log.action)}
                            {getStatusBadge(log.status_code, log.error_message)}
                            <span className="text-sm font-normal text-muted-foreground ml-2">
                              {format(new Date(log.created_at), "MMM d, yyyy h:mm:ss a")}
                            </span>
                          </DialogTitle>
                        </DialogHeader>
                        
                        <div className="flex-1 overflow-auto space-y-4">
                          <div>
                            <h4 className="font-medium mb-1 text-sm">Endpoint</h4>
                            <div className="bg-muted p-2 rounded font-mono text-sm">
                              {log.endpoint}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium">Meeting ID:</span>{" "}
                              <span className="font-mono">{log.meeting_id || "N/A"}</span>
                            </div>
                            <div>
                              <span className="font-medium">Request ID:</span>{" "}
                              <span className="font-mono">{log.mentorship_request_id || "N/A"}</span>
                            </div>
                          </div>
                          
                          {log.error_message && (
                            <div>
                              <h4 className="font-medium mb-1 text-sm text-destructive">Error Message</h4>
                              <div className="bg-destructive/10 text-destructive p-2 rounded text-sm">
                                {log.error_message}
                              </div>
                            </div>
                          )}
                          
                          <div>
                            <h4 className="font-medium mb-1 text-sm">Request Payload (Masked)</h4>
                            <ScrollArea className="h-[200px]">
                              <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                {log.payload_masked
                                  ? JSON.stringify(log.payload_masked, null, 2)
                                  : "No payload"}
                              </pre>
                            </ScrollArea>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-1 text-sm">Response (Masked)</h4>
                            <ScrollArea className="h-[200px]">
                              <pre className="bg-muted p-3 rounded text-xs overflow-auto">
                                {log.response_masked
                                  ? JSON.stringify(log.response_masked, null, 2)
                                  : "No response"}
                              </pre>
                            </ScrollArea>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
